import { useCallback } from 'react';

import {
  type MeResponse,
  MeResponseSchema,
  type TradingAccount,
  TradingAccountSchema,
} from '../../api';
import { useApiClient } from '../../lib/api/api-context';
import { type ApiQueryResult, useApiQuery } from '../../lib/api/use-api-query';

/** E11's two reads and the reload after a write. */
export interface AccountReads {
  /** `GET /me` (M1 §5.1): the Telegram state, the channel and position counts. */
  readonly me: ApiQueryResult<MeResponse>;
  /** `GET /trading-account` (S6's route; 503 until S6 builds it, S8 R21). */
  readonly account: ApiQueryResult<TradingAccount>;
  /** Reads both again (after a write, S8 R6, R9). */
  readonly reload: () => void;
}

/**
 * `GET /me` and `GET /trading-account`, each once when E11 opens, in parallel (S8 R22, Proposal):
 * no polling, only Home polls (M7 R4). The Home feature does not export `useMe`, so E11 has its
 * own read (features may not import each other's internals).
 */
export function useAccountReads(): AccountReads {
  const api = useApiClient();
  const loadMe = useCallback(
    (signal: AbortSignal) => api.get('/me', MeResponseSchema, signal),
    [api],
  );
  const loadAccount = useCallback(
    (signal: AbortSignal) => api.get('/trading-account', TradingAccountSchema, signal),
    [api],
  );
  const me = useApiQuery(loadMe);
  const account = useApiQuery(loadAccount);
  const { refetch: refetchMe } = me;
  const { refetch: refetchAccount } = account;
  const reload = useCallback(() => {
    refetchMe();
    refetchAccount();
  }, [refetchMe, refetchAccount]);
  return { me, account, reload };
}
